import { DA } from '../../theme/palette';

/**
 * Le voile doit-il recouvrir le bureau ?
 *
 * <p>Il tombe quand le serveur dit l'essai terminé sans formule souscrite :
 * c'est lui qui compte les trente jours, le navigateur ne refait pas le
 * calcul. La page Abonnement reste ouverte — c'est par elle qu'on en sort —,
 * et les ouvriers, qui pointent sur le chantier, ne sont jamais arrêtés.
 *
 * @param {object} contexte ce que l'application sait de l'écran
 * @param {object|null} contexte.soc société, telle que le serveur l'a rendue
 * @param {string|null} contexte.role rôle de la session, nul si personne n'est connecté
 * @param {string} contexte.nav écran affiché
 * @returns {boolean} vrai si le voile doit s'afficher
 */
export function voileFinEssaiVisible({ soc, role, nav }) {
  if (!soc || !role || role === 'ouvrier') return false;
  if (nav === 'abonnement' || nav === 'ouvrier') return false;
  return soc.abonnement?.etat === 'ESSAI_TERMINE';
}

const bouton = {
  border: 'none',
  color: '#fff',
  borderRadius: 12,
  fontWeight: 800,
  cursor: 'pointer',
  width: 260,
};

/**
 * L'écran qui recouvre l'application une fois l'essai terminé.
 *
 * <p>Le patron y trouve les formules et la confirmation de sa souscription :
 * « J'ai souscrit » ne débloque rien par lui-même, il mène à la page
 * Abonnement où la formule se confirme, et c'est cette confirmation,
 * enregistrée par le serveur, qui lève le voile. Les autres rôles ne peuvent
 * pas engager l'entreprise : ils sont invités à prévenir leur patron.
 *
 * @param {object} props propriétés du composant
 * @param {boolean} props.patron vrai si le compte peut souscrire
 * @param {() => void} props.onFormules ouvre la page Abonnement
 * @param {() => void} props.onConfirmer ouvre la page Abonnement pour confirmer la formule souscrite
 * @param {() => void} [props.onDeconnexion] ferme la session
 * @returns {import('react').ReactElement} le voile
 */
export function VoileFinEssai({ patron, onFormules, onConfirmer, onDeconnexion }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="voile-fin-essai-titre"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(15,22,38,.94)',
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 42, marginBottom: 12 }} aria-hidden="true">
        ⏳
      </div>
      <div
        id="voile-fin-essai-titre"
        style={{ color: '#fff', fontSize: 18, fontWeight: 800, marginBottom: 8 }}
      >
        Ton essai gratuit de 30 jours est terminé
      </div>
      {patron ? (
        <>
          <div
            style={{
              color: 'rgba(255,255,255,.7)',
              fontSize: 12,
              marginBottom: 22,
              maxWidth: 300,
              lineHeight: 1.6,
            }}
          >
            Choisis une formule pour continuer à utiliser SwissTech et garder l&apos;accès à tes
            devis, factures et chantiers.
          </div>
          <button
            type="button"
            onClick={onFormules}
            style={{
              ...bouton,
              background: DA,
              padding: '13px 22px',
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            Voir les formules d&apos;abonnement
          </button>
          <button
            type="button"
            onClick={onConfirmer}
            style={{
              ...bouton,
              background: 'transparent',
              border: '1px solid rgba(255,255,255,.3)',
              padding: '11px 20px',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            J&apos;ai souscrit — activer l&apos;accès
          </button>
        </>
      ) : (
        <div
          style={{
            color: 'rgba(255,255,255,.7)',
            fontSize: 12,
            marginBottom: 22,
            maxWidth: 300,
            lineHeight: 1.6,
          }}
        >
          L&apos;abonnement de l&apos;entreprise doit être souscrit pour continuer. Contacte ton
          patron : lui seul peut choisir une formule. Tes données sont conservées.
        </div>
      )}
      {typeof onDeconnexion === 'function' && (
        <button
          type="button"
          onClick={onDeconnexion}
          style={{
            marginTop: 18,
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,.55)',
            fontSize: 11,
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          Se déconnecter
        </button>
      )}
    </div>
  );
}
